(() => {
  'use strict';

  const KG_PER_LB = 0.45359237;

  /*
   * Nine-point Body Condition Score estimate:
   *
   * BCS 5 = ideal.
   * Each point above or below 5 represents approximately 10%
   * above or below ideal body weight.
   *
   * Examples:
   * BCS 7: current weight is estimated at 120% of ideal.
   * BCS 3: current weight is estimated at 80% of ideal.
   */
  const BCS_WEIGHT_DIFFERENCE_PER_POINT = 0.1;

  /*
   * A conservative starting pace for planned weight loss.
   * The displayed figure is 1% of current body weight per week.
   */
  const SAFE_WEEKLY_WEIGHT_LOSS_RATE = 0.01;

  const BCS_CONTENT = {
    1: {
      status: 'lean',
      title: 'Severely underweight',
      cue: 'Ribs, spine and pelvic bones are very prominent.',
      description:
        '<strong>BCS 1 — Emaciated.</strong> Ribs, spine and pelvic bones are clearly visible from a distance. There is almost no body fat and very little muscle covering.',
      topWaist: 0.48,
      sideTuck: 0.5,
      bodyWidth: 0.62
    },
    2: {
      status: 'lean',
      title: 'Very underweight',
      cue: 'Bones are easy to see, with a very narrow waist.',
      description:
        '<strong>BCS 2 — Very thin.</strong> Ribs and other bony points are easily visible. The waist is sharply defined and there is minimal fat covering.',
      topWaist: 0.56,
      sideTuck: 0.58,
      bodyWidth: 0.68
    },
    3: {
      status: 'lean',
      title: 'Underweight',
      cue: 'Ribs are easy to feel and the waist is strongly defined.',
      description:
        '<strong>BCS 3 — Thin.</strong> Ribs are easy to feel and may be visible. There is an obvious waist from above and a pronounced abdominal tuck from the side.',
      topWaist: 0.65,
      sideTuck: 0.68,
      bodyWidth: 0.74
    },
    4: {
      status: 'lean',
      title: 'Slightly under ideal',
      cue: 'A clear waist and abdominal tuck are present.',
      description:
        '<strong>BCS 4 — Lean.</strong> Ribs are easy to feel beneath a thin fat covering. The waist and abdominal tuck are clearly visible.',
      topWaist: 0.76,
      sideTuck: 0.8,
      bodyWidth: 0.84
    },
    5: {
      status: 'ideal',
      title: 'Ideal body condition',
      cue: 'Ribs are easy to feel, with a visible waist and belly tuck.',
      description:
        '<strong>BCS 5 — Ideal.</strong> Ribs are easy to feel beneath a light covering. A waist is visible behind the ribs from above and the abdomen tucks upward from the side.',
      topWaist: 0.84,
      sideTuck: 0.88,
      bodyWidth: 0.9
    },
    6: {
      status: 'over',
      title: 'Slightly above ideal',
      cue: 'The waist is present but less obvious.',
      description:
        '<strong>BCS 6 — Slightly overweight.</strong> Ribs can still be felt, but there is a noticeable fat covering. The waist and abdominal tuck are less defined.',
      topWaist: 0.91,
      sideTuck: 0.94,
      bodyWidth: 0.98
    },
    7: {
      status: 'over',
      title: 'Overweight',
      cue: 'The waist is difficult to see and the belly is more rounded.',
      description:
        '<strong>BCS 7 — Overweight.</strong> Ribs are difficult to feel beneath a heavier fat covering. The waist is poorly visible and fat deposits may be noticeable around the abdomen or lower back.',
      topWaist: 0.98,
      sideTuck: 1,
      bodyWidth: 1.08
    },
    8: {
      status: 'over',
      title: 'Very overweight',
      cue: 'There is little or no visible waist.',
      description:
        '<strong>BCS 8 — Obese.</strong> Ribs are difficult to feel. The waist is absent and the abdomen appears broad or rounded, with obvious fat deposits.',
      topWaist: 1.06,
      sideTuck: 1.08,
      bodyWidth: 1.18
    },
    9: {
      status: 'over',
      title: 'Severely overweight',
      cue: 'The body is broadly rounded with marked fat deposits.',
      description:
        '<strong>BCS 9 — Severely obese.</strong> Ribs cannot be felt beneath a thick fat covering. The waist and abdominal tuck are absent, with marked fat deposits around the chest, spine, abdomen and tail base.',
      topWaist: 1.14,
      sideTuck: 1.15,
      bodyWidth: 1.28
    }
  };

  class SIdealWeightCalculator extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.state = {
        species: this.getInitialSpecies(),
        bodyConditionScore: this.getInitialBodyConditionScore(),
        previousWeightUnit: 'kg',
        calculated: false
      };

      this.cacheElements();
      this.bindEvents();
      this.initialise();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    cacheElements() {
      this.form = this.querySelector('[data-iwc-form]');

      this.speciesInputs = this.querySelectorAll('[name="species"]');
      this.weightUnitInputs = this.querySelectorAll('[name="weightUnit"]');
      this.bcsInputs = this.querySelectorAll('[name="bodyConditionScore"]');

      this.weightInput = this.querySelector('[data-iwc-weight]');
      this.bcsValue = this.querySelector('[data-iwc-bcs-value]');

      this.topIllustration = this.querySelector(
        '[data-iwc-top-illustration]'
      );

      this.sideIllustration = this.querySelector(
        '[data-iwc-side-illustration]'
      );

      this.illustrationCue = this.querySelector(
        '[data-iwc-illustration-cue]'
      );

      this.bcsDescription = this.querySelector(
        '[data-iwc-bcs-description]'
      );

      this.howTo = this.querySelector('[data-iwc-howto]');
      this.howToToggle = this.querySelector('[data-iwc-howto-toggle]');
      this.howToContent = this.querySelector('[data-iwc-howto-content]');

      this.errorsWrap = this.querySelector('[data-iwc-errors]');

      this.results = this.querySelector('[data-iwc-results]');
      this.whoLine = this.querySelector('[data-iwc-who-line]');

      this.idealWeightValue = this.querySelector(
        '[data-iwc-ideal-weight]'
      );

      this.resultUnit = this.querySelector('[data-iwc-result-unit]');
      this.verdict = this.querySelector('[data-iwc-verdict]');

      this.changeLabel = this.querySelector('[data-iwc-change-label]');
      this.changeValue = this.querySelector('[data-iwc-change-value]');
      this.changeUnit = this.querySelector('[data-iwc-change-unit]');

      this.percentOff = this.querySelector('[data-iwc-percent-off]');

      this.safePaceCard = this.querySelector('[data-iwc-safe-pace-card]');
      this.safePaceValue = this.querySelector('[data-iwc-safe-pace]');
      this.safePaceUnit = this.querySelector('[data-iwc-safe-pace-unit]');

      this.goalTimeCard = this.querySelector('[data-iwc-goal-time-card]');
      this.goalTimeValue = this.querySelector('[data-iwc-goal-time]');

      this.guidance = this.querySelector('[data-iwc-guidance]');
      this.warning = this.querySelector('[data-iwc-warning]');

      this.primaryCta = this.querySelector('[data-iwc-primary-cta]');
      this.restartButton = this.querySelector('[data-iwc-restart]');
    }

    bindEvents() {
      if (!this.signal) return;

      this.form?.addEventListener(
        'submit',
        (event) => this.onSubmit(event),
        {
          signal: this.signal
        }
      );

      this.speciesInputs.forEach((input) => {
        input.addEventListener(
          'change',
          (event) => this.onSpeciesChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.weightUnitInputs.forEach((input) => {
        input.addEventListener(
          'change',
          (event) => this.onWeightUnitChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.bcsInputs.forEach((input) => {
        input.addEventListener(
          'change',
          (event) => this.onBodyConditionChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.weightInput?.addEventListener(
        'input',
        () => this.recalculateIfReady(),
        {
          signal: this.signal
        }
      );

      this.howToToggle?.addEventListener(
        'click',
        () => this.toggleHowToGuide(),
        {
          signal: this.signal
        }
      );

      this.restartButton?.addEventListener(
        'click',
        () => this.restart(),
        {
          signal: this.signal
        }
      );
    }

    initialise() {
      this.setCheckedSpecies(this.state.species);
      this.setCheckedBodyConditionScore(this.state.bodyConditionScore);

      this.state.previousWeightUnit = this.getSelectedValue(
        'weightUnit',
        'kg'
      );

      this.applyStaticLinks();
      this.updateBodyConditionInterface();
      this.clearErrors();
      this.hideResults();
    }

    applyStaticLinks() {
      const primaryCtaLink = String(
        this.dataset.primaryCtaLink || ''
      ).trim();

      if (primaryCtaLink && this.primaryCta) {
        this.primaryCta.href = primaryCtaLink;
      }
    }

    getInitialSpecies() {
      const configuredSpecies = String(
        this.dataset.defaultSpecies || ''
      )
        .trim()
        .toLowerCase();

      if (configuredSpecies === 'dog' || configuredSpecies === 'cat') {
        return configuredSpecies;
      }

      return (
        this.querySelector('[name="species"]:checked')?.value ||
        'dog'
      );
    }

    getInitialBodyConditionScore() {
      const configuredScore = Number(this.dataset.defaultBcs || 0);
      const checkedScore = Number(
        this.querySelector('[name="bodyConditionScore"]:checked')?.value ||
          0
      );

      if (
        Number.isInteger(configuredScore) &&
        configuredScore >= 1 &&
        configuredScore <= 9
      ) {
        return configuredScore;
      }

      if (
        Number.isInteger(checkedScore) &&
        checkedScore >= 1 &&
        checkedScore <= 9
      ) {
        return checkedScore;
      }

      return 5;
    }

    setCheckedSpecies(species) {
      this.speciesInputs.forEach((input) => {
        input.checked = input.value === species;
      });
    }

    setCheckedBodyConditionScore(score) {
      this.bcsInputs.forEach((input) => {
        input.checked = Number(input.value) === Number(score);
      });
    }

    getSelectedValue(name, fallback = '') {
      return (
        this.querySelector(`[name="${name}"]:checked`)?.value ||
        fallback
      );
    }

    onSpeciesChange(event) {
      const species = String(event.target?.value || '');

      if (species !== 'dog' && species !== 'cat') {
        return;
      }

      this.state.species = species;
      this.updateBodyConditionInterface();
      this.recalculateIfReady();
    }

    onBodyConditionChange(event) {
      const score = Number(event.target?.value || 0);

      if (!Number.isInteger(score) || score < 1 || score > 9) {
        return;
      }

      this.state.bodyConditionScore = score;
      this.updateBodyConditionInterface();
      this.recalculateIfReady();
    }

    onWeightUnitChange(event) {
      const nextUnit = String(event.target?.value || '');
      const previousUnit = this.state.previousWeightUnit;

      if (nextUnit !== 'kg' && nextUnit !== 'lb') {
        return;
      }

      this.convertWeightInput(
        this.weightInput,
        previousUnit,
        nextUnit
      );

      this.state.previousWeightUnit = nextUnit;
      this.recalculateIfReady();
    }

    convertWeightInput(input, fromUnit, toUnit) {
      if (
        !input ||
        !input.value ||
        !fromUnit ||
        !toUnit ||
        fromUnit === toUnit
      ) {
        return;
      }

      const numericValue = Number(input.value);

      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return;
      }

      const kilograms =
        fromUnit === 'lb'
          ? numericValue * KG_PER_LB
          : numericValue;

      const converted =
        toUnit === 'lb'
          ? kilograms / KG_PER_LB
          : kilograms;

      input.value = this.formatInputNumber(converted);
    }

    updateBodyConditionInterface() {
      const score = this.state.bodyConditionScore;
      const content = BCS_CONTENT[score] || BCS_CONTENT[5];

      if (this.bcsValue) {
        this.bcsValue.textContent = `${score} / 9`;
      }

      if (this.illustrationCue) {
        this.illustrationCue.textContent = content.cue;
      }

      if (this.bcsDescription) {
        this.bcsDescription.innerHTML = content.description;
      }

      this.renderBodyIllustrations();
    }

    renderBodyIllustrations() {
      const content =
        BCS_CONTENT[this.state.bodyConditionScore] ||
        BCS_CONTENT[5];

      if (this.topIllustration) {
        this.topIllustration.innerHTML = this.createTopViewSvg(
          this.state.species,
          content
        );
      }

      if (this.sideIllustration) {
        this.sideIllustration.innerHTML = this.createSideViewSvg(
          this.state.species,
          content
        );
      }
    }

    createTopViewSvg(species, content) {
      const isCat = species === 'cat';

      const torsoWidth = 74 * content.bodyWidth;
      const waistWidth = 54 * content.topWaist;

      const torsoLeft = 120 - torsoWidth / 2;
      const torsoRight = 120 + torsoWidth / 2;
      const waistLeft = 120 - waistWidth / 2;
      const waistRight = 120 + waistWidth / 2;

      const earMarkup = isCat
        ? `
          <path d="M94 35 L102 14 L112 36 Z" fill="#d7e5e4" stroke="#667085" stroke-width="2"/>
          <path d="M128 36 L138 14 L146 35 Z" fill="#d7e5e4" stroke="#667085" stroke-width="2"/>
        `
        : `
          <ellipse cx="98" cy="31" rx="10" ry="18" fill="#d7e5e4" stroke="#667085" stroke-width="2"/>
          <ellipse cx="142" cy="31" rx="10" ry="18" fill="#d7e5e4" stroke="#667085" stroke-width="2"/>
        `;

      return `
        <svg
          viewBox="0 0 240 220"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="${isCat ? 'Cat' : 'Dog'} body shape viewed from above"
        >
          <g fill="none" stroke-linecap="round" stroke-linejoin="round">
            ${earMarkup}

            <ellipse
              cx="120"
              cy="45"
              rx="${isCat ? 25 : 28}"
              ry="${isCat ? 26 : 29}"
              fill="#d7e5e4"
              stroke="#667085"
              stroke-width="2.2"
            />

            <path
              d="
                M ${torsoLeft} 66
                C ${torsoLeft - 8} 78,
                  ${torsoLeft - 8} 101,
                  ${waistLeft} 119
                C ${waistLeft - 2} 137,
                  ${torsoLeft + 4} 157,
                  ${torsoLeft + 9} 176
                C ${torsoLeft + 19} 194,
                  ${torsoRight - 19} 194,
                  ${torsoRight - 9} 176
                C ${torsoRight - 4} 157,
                  ${waistRight + 2} 137,
                  ${waistRight} 119
                C ${torsoRight + 8} 101,
                  ${torsoRight + 8} 78,
                  ${torsoRight} 66
                Z
              "
              fill="#d7e5e4"
              stroke="#667085"
              stroke-width="2.4"
            />

            <path
              d="M120 193 C121 207, ${isCat ? 139 : 132} 207, ${isCat ? 151 : 137} 195"
              stroke="#667085"
              stroke-width="${isCat ? 6 : 8}"
            />

            <path
              d="M ${torsoLeft + 7} 91 L ${torsoLeft - 15} 105"
              stroke="#667085"
              stroke-width="8"
            />

            <path
              d="M ${torsoRight - 7} 91 L ${torsoRight + 15} 105"
              stroke="#667085"
              stroke-width="8"
            />

            <path
              d="M ${torsoLeft + 14} 164 L ${torsoLeft - 5} 187"
              stroke="#667085"
              stroke-width="8"
            />

            <path
              d="M ${torsoRight - 14} 164 L ${torsoRight + 5} 187"
              stroke="#667085"
              stroke-width="8"
            />
          </g>
        </svg>
      `;
    }

    createSideViewSvg(species, content) {
      const isCat = species === 'cat';

      const bodyHeight = 68 * content.bodyWidth;
      const bodyTop = 100 - bodyHeight / 2;
      const bodyBottom = 100 + bodyHeight / 2;

      const tuckAmount = 26 * content.sideTuck;
      const bellyPoint = Math.min(
        146,
        bodyBottom - tuckAmount
      );

      const earMarkup = isCat
        ? `
          <path d="M59 72 L68 47 L77 72 Z" fill="#d7e5e4" stroke="#667085" stroke-width="2"/>
          <path d="M78 72 L89 49 L93 76 Z" fill="#d7e5e4" stroke="#667085" stroke-width="2"/>
        `
        : `
          <ellipse cx="68" cy="67" rx="11" ry="20" transform="rotate(-20 68 67)" fill="#d7e5e4" stroke="#667085" stroke-width="2"/>
        `;

      return `
        <svg
          viewBox="0 0 260 220"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="${isCat ? 'Cat' : 'Dog'} body shape viewed from the side"
        >
          <g fill="none" stroke-linecap="round" stroke-linejoin="round">
            ${earMarkup}

            <ellipse
              cx="72"
              cy="82"
              rx="${isCat ? 27 : 30}"
              ry="${isCat ? 24 : 27}"
              fill="#d7e5e4"
              stroke="#667085"
              stroke-width="2.2"
            />

            <path
              d="
                M 91 82
                C 108 ${bodyTop - 10},
                  145 ${bodyTop - 8},
                  183 ${bodyTop}
                C 208 ${bodyTop + 5},
                  220 77,
                  215 106
                C 211 126,
                  198 137,
                  181 ${bellyPoint}
                C 156 ${bellyPoint - 4},
                  132 ${bodyBottom},
                  105 ${bodyBottom - 4}
                C 91 ${bodyBottom - 13},
                  82 110,
                  91 82
                Z
              "
              fill="#d7e5e4"
              stroke="#667085"
              stroke-width="2.4"
            />

            <path
              d="M205 86 C229 67, ${isCat ? 242 : 236} 78, ${isCat ? 231 : 226} 99"
              stroke="#667085"
              stroke-width="${isCat ? 6 : 8}"
            />

            <path
              d="M112 ${bodyBottom - 5} L108 189"
              stroke="#667085"
              stroke-width="9"
            />

            <path
              d="M132 ${bodyBottom - 3} L137 189"
              stroke="#667085"
              stroke-width="9"
            />

            <path
              d="M184 ${bellyPoint - 1} L178 189"
              stroke="#667085"
              stroke-width="9"
            />

            <path
              d="M201 ${bodyBottom - 10} L204 189"
              stroke="#667085"
              stroke-width="9"
            />

            <path
              d="M52 85 C43 89, 38 88, 33 84"
              stroke="#667085"
              stroke-width="4"
            />
          </g>
        </svg>
      `;
    }

    toggleHowToGuide() {
      if (
        !this.howTo ||
        !this.howToToggle ||
        !this.howToContent
      ) {
        return;
      }

      const isOpen = !this.howToContent.hidden;

      this.howToContent.hidden = isOpen;
      this.howTo.classList.toggle('is-open', !isOpen);

      this.howToToggle.setAttribute(
        'aria-expanded',
        String(!isOpen)
      );
    }

    onSubmit(event) {
      event.preventDefault();

      this.calculate({
        shouldScroll: true,
        showErrors: true
      });
    }

    recalculateIfReady() {
      if (!this.state.calculated) {
        return;
      }

      this.calculate({
        shouldScroll: false,
        showErrors: false
      });
    }

    calculate({
      shouldScroll = false,
      showErrors = true
    } = {}) {
      this.clearErrors();

      const parsed = this.parseInputs();

      if (!parsed.ok) {
        if (showErrors) {
          this.renderErrors(parsed.errors);
        }

        return;
      }

      const result = this.calculateResult(parsed.data);

      this.renderResult(result);
      this.showResults();

      if (shouldScroll) {
        requestAnimationFrame(() => {
          this.results?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        });
      }
    }

    parseInputs() {
      const errors = [];

      const species = this.state.species;
      const bodyConditionScore = this.state.bodyConditionScore;

      const weight = Number(this.weightInput?.value || 0);
      const weightUnit = this.getSelectedValue('weightUnit', 'kg');

      if (species !== 'dog' && species !== 'cat') {
        errors.push('Choose dog or cat.');
      }

      if (!Number.isFinite(weight) || weight <= 0) {
        errors.push("Enter your pet's current weight.");
      }

      if (
        !Number.isInteger(bodyConditionScore) ||
        bodyConditionScore < 1 ||
        bodyConditionScore > 9
      ) {
        errors.push('Choose a Body Condition Score from 1 to 9.');
      }

      const weightKg = this.toKg(weight, weightUnit);

      if (
        Number.isFinite(weightKg) &&
        species === 'dog' &&
        weightKg > 120
      ) {
        errors.push(
          "That weight looks unusually high for a dog. Please double-check it."
        );
      }

      if (
        Number.isFinite(weightKg) &&
        species === 'cat' &&
        weightKg > 30
      ) {
        errors.push(
          "That weight looks unusually high for a cat. Please double-check it."
        );
      }

      if (errors.length) {
        return {
          ok: false,
          errors
        };
      }

      return {
        ok: true,
        data: {
          species,
          weight,
          weightKg,
          weightUnit,
          bodyConditionScore
        }
      };
    }

    calculateResult(data) {
      const scoreDifference =
        data.bodyConditionScore - 5;

      const estimatedDifference =
        scoreDifference *
        BCS_WEIGHT_DIFFERENCE_PER_POINT;

      let idealWeightKg;

      if (scoreDifference === 0) {
        idealWeightKg = data.weightKg;
      } else {
        idealWeightKg =
          data.weightKg /
          (1 + estimatedDifference);
      }

      idealWeightKg = Math.max(
        0.1,
        idealWeightKg
      );

      const changeKg =
        idealWeightKg - data.weightKg;

      const absoluteChangeKg =
        Math.abs(changeKg);

      const percentOffIdeal =
        idealWeightKg > 0
          ? (absoluteChangeKg / idealWeightKg) * 100
          : 0;

      const status =
        data.bodyConditionScore < 5
          ? 'lean'
          : data.bodyConditionScore > 5
            ? 'over'
            : 'ideal';

      const safeWeeklyLossKg =
        status === 'over'
          ? data.weightKg *
            SAFE_WEEKLY_WEIGHT_LOSS_RATE
          : null;

      const estimatedWeeksToGoal =
        status === 'over' &&
        safeWeeklyLossKg > 0
          ? absoluteChangeKg / safeWeeklyLossKg
          : null;

      return {
        ...data,
        status,
        idealWeightKg,
        changeKg,
        absoluteChangeKg,
        percentOffIdeal,
        safeWeeklyLossKg,
        estimatedWeeksToGoal,
        content:
          BCS_CONTENT[data.bodyConditionScore] ||
          BCS_CONTENT[5]
      };
    }

    renderResult(result) {
      const resultWeight =
        this.fromKg(
          result.idealWeightKg,
          result.weightUnit
        );

      const change =
        this.fromKg(
          result.absoluteChangeKg,
          result.weightUnit
        );

      const unit =
        result.weightUnit === 'lb'
          ? 'lb'
          : 'kg';

      this.renderWhoLine(result);

      if (this.idealWeightValue) {
        this.idealWeightValue.textContent =
          this.formatResultWeight(resultWeight);
      }

      if (this.resultUnit) {
        this.resultUnit.textContent = unit;
      }

      this.renderVerdict(result);
      this.renderChangeStatistics(result, change, unit);
      this.renderSafePace(result, unit);
      this.renderGuidance(result);
      this.renderWarning(result);
    }

    renderWhoLine(result) {
      if (!this.whoLine) {
        return;
      }

      const speciesLabel =
        result.species === 'cat'
          ? 'Cat'
          : 'Dog';

      this.whoLine.textContent =
        `${speciesLabel} · ${this.formatDecimal(
          result.weight,
          1
        )} ${result.weightUnit} · BCS ${result.bodyConditionScore}/9`;
    }

    renderVerdict(result) {
      if (!this.verdict) {
        return;
      }

      this.verdict.dataset.status = result.status;

      if (result.status === 'ideal') {
        this.verdict.textContent =
          'On target — maintain';

        return;
      }

      if (result.status === 'lean') {
        this.verdict.textContent =
          result.bodyConditionScore <= 2
            ? 'Significantly under ideal'
            : 'Below ideal weight';

        return;
      }

      this.verdict.textContent =
        result.bodyConditionScore >= 8
          ? 'Significantly above ideal'
          : 'Above ideal weight';
    }

    renderChangeStatistics(result, change, unit) {
      if (
        !this.changeLabel ||
        !this.changeValue ||
        !this.changeUnit ||
        !this.percentOff
      ) {
        return;
      }

      if (result.status === 'ideal') {
        this.changeLabel.textContent = 'On target';
        this.changeValue.textContent = '0';
      } else if (result.status === 'lean') {
        this.changeLabel.textContent = 'Estimated gain';
        this.changeValue.textContent =
          this.formatResultWeight(change);
      } else {
        this.changeLabel.textContent = 'Estimated loss';
        this.changeValue.textContent =
          this.formatResultWeight(change);
      }

      this.changeUnit.textContent = unit;

      this.percentOff.textContent =
        `${Math.round(result.percentOffIdeal)}%`;
    }

    renderSafePace(result, unit) {
      if (
        !this.safePaceCard ||
        !this.safePaceValue ||
        !this.safePaceUnit ||
        !this.goalTimeCard ||
        !this.goalTimeValue
      ) {
        return;
      }

      if (
        result.status !== 'over' ||
        !result.safeWeeklyLossKg ||
        !result.estimatedWeeksToGoal
      ) {
        this.safePaceCard.hidden = true;
        this.goalTimeCard.hidden = true;
        this.safePaceValue.textContent = '';
        this.goalTimeValue.textContent = '';

        return;
      }

      const safePace =
        this.fromKg(
          result.safeWeeklyLossKg,
          result.weightUnit
        );

      this.safePaceValue.textContent =
        this.formatResultWeight(safePace);

      this.safePaceUnit.textContent =
        `${unit}/week`;

      this.goalTimeValue.textContent =
        String(
          Math.max(
            1,
            Math.ceil(result.estimatedWeeksToGoal)
          )
        );

      this.safePaceCard.hidden = false;
      this.goalTimeCard.hidden = false;
    }

    renderGuidance(result) {
      if (!this.guidance) {
        return;
      }

      if (result.status === 'ideal') {
        this.guidance.innerHTML = `
          <strong>Maintain the current body condition.</strong>
          Keep portions consistent, include treats in the daily total and
          recheck weight and body condition regularly.
        `;

        return;
      }

      if (result.status === 'lean') {
        this.guidance.innerHTML = `
          <strong>The estimate suggests your pet is below ideal weight.</strong>
          Do not simply increase food without considering why they are thin.
          Confirm the body-condition score and discuss an appropriate feeding
          plan with your vet.
        `;

        return;
      }

      this.guidance.innerHTML = `
        <strong>The estimate suggests your pet is above ideal weight.</strong>
        Aim for gradual change rather than a sudden food restriction. Measure
        meals accurately, include treats in the daily total and reassess body
        condition as weight changes.
      `;
    }

    renderWarning(result) {
      if (!this.warning) {
        return;
      }

      if (
        result.bodyConditionScore === 1 ||
        result.bodyConditionScore === 2
      ) {
        this.warning.innerHTML = `
          <strong>Veterinary assessment recommended.</strong>
          A very low Body Condition Score may reflect inadequate nutrition,
          dental disease, digestive disease, pain or another medical problem.
        `;

        this.warning.hidden = false;
        return;
      }

      if (
        result.bodyConditionScore === 8 ||
        result.bodyConditionScore === 9
      ) {
        this.warning.innerHTML = `
          <strong>Speak to your vet before starting a major diet change.</strong>
          Significant excess weight can affect joints, breathing, mobility and
          metabolic health. A structured plan is safer than rapid restriction.
        `;

        this.warning.hidden = false;
        return;
      }

      this.warning.innerHTML = '';
      this.warning.hidden = true;
    }

    renderErrors(errors) {
      if (!this.errorsWrap) {
        return;
      }

      this.errorsWrap.innerHTML = errors
        .map(
          (error) =>
            `<div>${this.escapeHtml(error)}</div>`
        )
        .join('');

      this.errorsWrap.hidden = false;

      this.errorsWrap.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }

    clearErrors() {
      if (!this.errorsWrap) {
        return;
      }

      this.errorsWrap.innerHTML = '';
      this.errorsWrap.hidden = true;
    }

    showResults() {
      if (!this.results) {
        return;
      }

      this.results.hidden = false;
      this.state.calculated = true;
    }

    hideResults() {
      if (!this.results) {
        return;
      }

      this.results.hidden = true;
      this.state.calculated = false;
    }

    restart() {
      this.hideResults();
      this.clearErrors();

      requestAnimationFrame(() => {
        this.form?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        this.weightInput?.focus({
          preventScroll: true
        });
      });
    }

    toKg(value, unit) {
      const numericValue = Number(value);

      return unit === 'lb'
        ? numericValue * KG_PER_LB
        : numericValue;
    }

    fromKg(value, unit) {
      const numericValue = Number(value);

      return unit === 'lb'
        ? numericValue / KG_PER_LB
        : numericValue;
    }

    formatInputNumber(value) {
      const numericValue = Number(value);

      if (
        !Number.isFinite(numericValue) ||
        numericValue <= 0
      ) {
        return '';
      }

      if (numericValue >= 100) {
        return String(Math.round(numericValue));
      }

      return numericValue
        .toFixed(2)
        .replace(/\.?0+$/, '');
    }

    formatResultWeight(value) {
      const numericValue = Number(value);

      if (!Number.isFinite(numericValue)) {
        return '0';
      }

      if (numericValue >= 100) {
        return String(Math.round(numericValue));
      }

      if (numericValue >= 10) {
        return numericValue
          .toFixed(1)
          .replace(/\.0$/, '');
      }

      return numericValue
        .toFixed(2)
        .replace(/\.00$/, '')
        .replace(/(\.\d)0$/, '$1');
    }

    formatDecimal(value, digits = 1) {
      const numericValue = Number(value);

      if (!Number.isFinite(numericValue)) {
        return '0';
      }

      return numericValue
        .toFixed(digits)
        .replace(/\.?0+$/, '');
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  if (!customElements.get('s-ideal-weight-calculator')) {
    customElements.define(
      's-ideal-weight-calculator',
      SIdealWeightCalculator
    );
  }
})();
